<script setup>
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue'
import { Icon } from '@iconify/vue'
import { Tooltip } from 'floating-vue'
import AppButton from '@/components/atoms/AppButton'
import { useSidePanelResize } from '@/hooks/useSidePanelResize'

defineOptions({ inheritAttrs: false })

/**
 * AppSidePanel — page-level side column (Figma 28702:67216).
 *
 * A flex sibling of the page's scroll region: it slides in and takes layout
 * width. Below `2xl` it is a fixed drawer over the page, under the app chrome.
 * The parent must be a flex row with `overflow-hidden`.
 *
 * The body scrolls between a pinned header and footer. With `scrollHeader` the
 * panel itself scrolls, so the header goes up with the content and only the
 * footer stays put: for phones, where a pinned header costs too much height.
 */
const props = defineProps({
  open:        { type: Boolean, required: true },
  title:       { type: String,  default: '' },
  description: { type: String,  default: '' },
  side:        { type: String,  default: 'right', validator: (side) => ['right', 'left'].includes(side) },
  width:       { type: Number,  default: 355 },  // px, from `md` up; full width below
  resizable:   { type: Boolean, default: false },
  minWidth:    { type: Number, default: 280 },
  maxWidth:    { type: Number, default: 600 },
  insetTop:    { type: Number,  default: 0 },    // px; where the drawer starts, e.g. globalStore.appChromeHeight
  hideClose:   { type: Boolean, default: false },
  closeOnEscape: { type: Boolean, default: true },  // off while a panel floating over the page should take the key first
  scrollHeader: { type: Boolean, default: false },  // the header scrolls with the content; only the footer stays put
})

const emit = defineEmits(['close', 'update:width'])

// Literal strings so Tailwind's scanner generates them.
const SIDE_CLASSES = {
  right: {
    panel: 'border-l md:rounded-l-2xl max-2xl:right-0',
    hidden: '-mr-[var(--side-panel-width)] max-2xl:mr-0 max-2xl:translate-x-full',
  },
  left: {
    panel: 'border-r md:rounded-r-2xl max-2xl:left-0',
    hidden: '-ml-[var(--side-panel-width)] max-2xl:ml-0 max-2xl:-translate-x-full',
  },
}

const titleId = useId()
const panel = ref(null)
const body = ref(null)
const resizeTooltip = ref(null)
// The scrolling element, exposed so a consumer that swaps its content can reset the scroll: the body, or the whole panel when the header scrolls too.
const scroller = computed(() => (props.scrollHeader ? panel.value : body.value))
const {
  width: panelWidth, minimum, maximum, enabled: resizeEnabled, resizing,
  hovered: resizeHovered, hoverY: resizeHoverY,
  start: startResize, hover: hoverResize, leave: leaveResize, keydown: resizeKeydown,
} = useSidePanelResize(props, emit)

// What the keyboard can reach, in DOM order.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const sideClasses = computed(() => SIDE_CLASSES[props.side] ?? SIDE_CLASSES.right)

const close = () => emit('close')

// Opening moves the keyboard into the panel; one that mounts open (a persisted column) leaves focus where the page put it.
watch(() => props.open, (open) => {
  if (open) panel.value?.querySelector(FOCUSABLE)?.focus({ preventScroll: true })
}, { flush: 'post' })

// Shown while something that takes Escape without marking it handled is open: a dialog, a grid cell being edited, a grid popup.
const ESCAPE_OWNERS = '[role="dialog"], .ag-cell-inline-editing, .ag-cell-popup-editing, .ag-popup'
const isShown = (el) => el.getClientRects().length > 0

// Escape closes the panel from anywhere on the page unless something above it took the key.
// Captured before a grid editor reacts; decided after the dispatch, once a dialog or menu that handled it has marked it `defaultPrevented`.
const onKeydown = (event) => {
  if (event.key !== 'Escape' || event.isComposing || !props.closeOnEscape) return
  if ([...document.querySelectorAll(ESCAPE_OWNERS)].some(isShown)) return
  setTimeout(() => {
    if (props.open && !event.defaultPrevented) close()
  })
}

watch(() => props.open, (open) => {
  if (open) document.addEventListener('keydown', onKeydown, true)
  else document.removeEventListener('keydown', onKeydown, true)
}, { immediate: true })

onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown, true))

// The tooltip's marker moved under the pointer; floating-vue re-measures only on its own props.
watch(resizeHoverY, () => resizeTooltip.value?.onResize(), { flush: 'post' })

defineExpose({ body: scroller })
</script>

<template>
  <Transition
    :duration="300"
    enter-active-class="transition-[margin,transform] duration-300"
    :enter-from-class="sideClasses.hidden"
    leave-active-class="transition-[margin,transform] duration-300"
    :leave-to-class="sideClasses.hidden"
  >
    <aside
      v-if="open"
      v-bind="$attrs"
      ref="panel"
      :aria-labelledby="title && !$slots.header ? titleId : undefined"
      class="group/panel relative flex flex-none flex-col w-full md:w-[var(--side-panel-width)] max-w-full bg-white max-2xl:fixed max-2xl:top-[var(--side-panel-top)] max-2xl:bottom-0 max-2xl:z-[998]"
      :class="[
        sideClasses.panel,
        scrollHeader ? 'overflow-y-auto overscroll-y-contain' : 'overflow-hidden',
        resizing ? 'border-tint-1 shadow-[0_0_5px_theme(colors.tint.1/60%)]' : 'border-gray-200 shadow-md',
      ]"
      :style="{ '--side-panel-width': `${panelWidth}px`, '--side-panel-top': `${insetTop}px` }"
    >
      <!-- Resize feedback (Figma 29792:56648 / 57844 / 59070): a grip once the panel is hovered, a tooltip beside the pointer on the edge, and a tint-1 edge with a glow in place of the shadow while dragging. -->
      <div
        v-if="resizeEnabled"
        role="separator"
        tabindex="0"
        aria-orientation="vertical"
        :aria-label="`Resize ${title || 'side panel'}`"
        :aria-valuemin="minimum"
        :aria-valuemax="maximum"
        :aria-valuenow="panelWidth"
        :aria-valuetext="`${panelWidth} pixels`"
        class="group/handle absolute inset-y-0 z-20 flex w-2 cursor-col-resize touch-none select-none items-center outline-none"
        :class="side === 'right' ? 'left-0' : 'right-0 justify-end'"
        @pointerenter="hoverResize"
        @pointermove="hoverResize"
        @pointerleave="leaveResize"
        @pointerdown="startResize"
        @keydown="resizeKeydown"
      >
        <!-- Fixed at its static position so the panel's overflow clip does not apply: the grip straddles the visible edge, 1px over the border line and 1px over the panel. -->
        <span
          class="pointer-events-none fixed h-12 w-0.5 rounded-full bg-gray-700 opacity-0 transition-opacity group-hover/panel:opacity-100 group-focus-visible/handle:opacity-100"
          :class="[side === 'right' ? '-ml-px' : '-mr-px', { 'opacity-100': resizing }]"
        />
        <!-- A 1px marker under the pointer anchors the tooltip level with it; a drag hides it. -->
        <Tooltip
          ref="resizeTooltip"
          class="pointer-events-none absolute inset-x-0 h-px"
          :style="{ top: `${resizeHoverY}px` }"
          :placement="side === 'right' ? 'left' : 'right'"
          :distance="20"
          :triggers="[]"
          :shown="resizeHovered && !resizing"
        >
          <template #popper>
            <span class="base-tooltip-2 py-2.5 shadow-md">Drag to resize</span>
          </template>
        </Tooltip>
      </div>
      <header
        v-if="title || $slots.header || !hideClose"
        class="grid flex-none grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 pl-6 pr-5 pt-4 pb-3"
      >
        <slot name="header">
          <h2 v-if="title" :id="titleId" class="text-lg font-semibold text-gray-900">{{ title }}</h2>
        </slot>
        <!-- The small box keeps the title row at the title's own height; -mr-2 cancels the button's padding so the glyph sits on the header's edge. -->
        <AppButton
          v-if="!hideClose"
          variant="link"
          :coloured="false"
          size="sm"
          class="col-start-2 -mr-2"
          :aria-label="title ? `Close ${title}` : 'Close'"
          @click="close"
        >
          <Icon icon="heroicons-solid:x" class="text-xl text-gray-600" />
        </AppButton>
        <p v-if="description" class="col-span-2 mt-1 text-sm text-gray-500">{{ description }}</p>
      </header>

      <!-- The bottom padding keeps the last section off the footer (Figma's scroll area ends 20px above it; 32 read as tight at 20). Its scroll ends at its own edges, not on the page under the drawer. -->
      <!-- When the panel is the scroller the body still grows to the footer, so short content can centre and the footer ends the panel. -->
      <div ref="body" class="pb-8" :class="scrollHeader ? 'grow' : 'flex-1 min-h-0 overflow-y-auto overscroll-y-contain'">
        <slot />
      </div>

      <!-- When the panel is the scroller, the footer rides on its bottom edge. -->
      <footer v-if="$slots.footer" class="flex flex-none flex-col gap-y-3 px-5 pt-3 pb-5" :class="{ 'sticky bottom-0 bg-white': scrollHeader }">
        <slot name="footer" :close="close" />
      </footer>
    </aside>
  </Transition>

  <!-- Covers embedded videos too, so crossing an iframe cannot swallow the drag. -->
  <Teleport to="body">
    <div v-if="resizing" class="fixed inset-0 z-[10000] cursor-col-resize select-none touch-none" aria-hidden="true" />
  </Teleport>
</template>
